import { ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  ConsolePaneActions,
  ConsolePaneBadges,
} from "@/components/layouts/console/pane-header-context";

interface PageLayoutProps {
  title: string;
  /** Total item count shown beside the title (e.g. number of resources) */
  count?: number;
  /** Rendered inline with the title (e.g. tab navigation) */
  navigation?: ReactNode;
  /** Right-side actions in the main header */
  actions?: ReactNode;
  /** Optional sticky bar below the main header (e.g. sort/filter controls) */
  subheader?: ReactNode;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
}

/**
 * The shared page shell: a pinned title row, optional tabs, actions and a
 * subheader, over a scrolling body.
 *
 * The two console slots render nothing in every other layout — their context
 * defaults to null and only ConsoleLayoutClient provides it — so this is
 * byte-for-byte the same output on Standard, Reader and Spaces. Console is the
 * one layout with no header bar on desktop, and this row is where its studio
 * status and header actions land. See layouts/console/pane-header-context.tsx.
 */
export default function PageLayout({
  title,
  count,
  navigation,
  actions,
  subheader,
  children,
  className,
  headerClassName,
}: PageLayoutProps) {
  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "sticky top-0 z-10 flex shrink-0 flex-col border-b border-border bg-background",
          headerClassName,
        )}
      >
        <div className="flex items-center justify-between px-6 py-3 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="shrink-0 font-title text-base font-semibold leading-6">
              {title}
            </h1>
            {count !== undefined && (
              <span
                className="shrink-0 text-sm text-muted-foreground"
                aria-label={`${count} total`}
              >
                {count}
              </span>
            )}
            {navigation && (
              <div className="flex items-center gap-1">{navigation}</div>
            )}
            <ConsolePaneBadges />
          </div>
          {/* Rendered unconditionally now: console supplies actions to pages
              that pass none of their own, and gating the row on `actions`
              would drop notifications and the profile card on exactly those
              pages. Empty it collapses to nothing — `gap` needs children. */}
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            <ConsolePaneActions />
          </div>
        </div>

        {subheader && (
          <div className="flex items-center justify-between border-t border-border px-6 py-2 sm:px-4">
            {subheader}
          </div>
        )}
      </div>

      <div
        className={cn("flex-1 overflow-y-auto px-6 py-6 sm:px-8", className)}
      >
        {children}
      </div>
    </div>
  );
}
